import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatDialog } from '@angular/material/dialog';
import { Subject, forkJoin, of } from 'rxjs';
import { catchError, takeUntil } from 'rxjs/operators';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTableModule } from '@angular/material/table';
import { MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatTabsModule } from '@angular/material/tabs';
import { MatBadgeModule } from '@angular/material/badge';
import { MatSnackBarModule, MatSnackBar } from '@angular/material/snack-bar';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatChipsModule } from '@angular/material/chips';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatSortModule } from '@angular/material/sort';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { PaymentService } from '../../api/services/payment.service';
import { OrderService } from '../../api/services/order.service';
import { Payment } from '../../api/models';
import { MatTableDataSource } from '@angular/material/table';
import { HeaderNavComponent } from "src/app/ui/header-nav/header-nav.component";
import { FooterComponent } from "src/app/ui/footer/footer.component";
import { PaymentProcessDialogComponent } from './payment-process-dialog/payment-process-dialog.component';
import { PaymentDetailDialogComponent } from './payment-detail-dialog/payment-detail-dialog.component';
import { PaymentRefundDialogComponent } from './payment-refund-dialog/payment-refund-dialog.component';
import { PaymentConfirmationDialogComponent } from './payment-confirmation-dialog/payment-confirmation-dialog.component';
import { PageEvent } from '@angular/material/paginator';
import { MatProgressSpinner } from "@angular/material/progress-spinner";

@Component({
  selector: 'app-payment-interface',
  standalone: true,
  imports: [
    CommonModule, MatCardModule, MatButtonModule, MatIconModule, MatTableModule,
    MatDialogModule, MatFormFieldModule, MatInputModule, MatSelectModule,
    MatTabsModule, MatBadgeModule, MatSnackBarModule, MatToolbarModule,
    MatChipsModule, MatPaginatorModule, MatSortModule, FormsModule, ReactiveFormsModule,
    HeaderNavComponent,
    FooterComponent,
    MatProgressSpinner
],
  templateUrl: './payment-interface.component.html',
  styles: []
})
export class PaymentInterfaceComponent implements OnInit, OnDestroy {
  payments: Payment[] = [];
  dataSource = new MatTableDataSource<Payment>();
  displayedColumns: string[] = ['paymentId', 'orderId', 'table', 'customer', 'amount', 'method', 'status', 'time', 'actions'];
  selectedTabIndex = 0;
  totalElements = 0;
  pageSize = 10;
  pageIndex = 0;
  isLoading = true;
  customerNames: Map<number, string> = new Map();
  
  stats: any = {
    totalRevenue: 0,
    totalTips: 0,
    totalTransactions: 0,
    pendingCount: 0,
    statusCounts: {}
  };

  searchQuery = '';
  searchInvoice = '';
  searchMethod = '';
  searchStartDate = '';
  searchEndDate = '';
  topMethods: any[] = [];

  private destroy$ = new Subject<void>();

  constructor(
    private paymentService: PaymentService,
    private orderService: OrderService,
    private snackBar: MatSnackBar,
    private router: Router,
    private dialog: MatDialog
  ) {}

  ngOnInit(): void {
    this.loadData();
    this.loadStats();
    this.loadTopMethods();
  }

  loadTopMethods(): void {
    this.paymentService.getTopMethodsByAmount(5)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (methods) => this.topMethods = methods,
        error: () => this.snackBar.open('Could not load payment statistics', 'Dismiss', { duration: 3000 })
      });
  }

  loadFailedPayments(): void {
    this.isLoading = true;
    this.paymentService.getFailedPayments().pipe(takeUntil(this.destroy$)).subscribe({
      next: (payments) => {
        this.payments = payments;
        this.dataSource.data = payments;
        this.totalElements = payments.length;
        this.isLoading = false;
      },
      error: () => this.isLoading = false
    });
  }

  loadRefundedPayments(): void {
    this.isLoading = true;
    this.paymentService.getRefundedPayments().pipe(takeUntil(this.destroy$)).subscribe({
      next: (payments) => {
        this.payments = payments;
        this.dataSource.data = payments;
        this.totalElements = payments.length;
        this.isLoading = false;
      },
      error: () => this.isLoading = false
    });
  }

  searchPayments(): void {
    if (!this.searchQuery && !this.searchInvoice && !this.searchMethod && !this.searchStartDate) {
      this.loadData();
      return;
    }
    this.isLoading = true;
    this.paymentService.searchPayments({
      transactionId: this.searchQuery || undefined,
      invoiceNumber: this.searchInvoice || undefined,
      paymentMethod: this.searchMethod || undefined,
      startDate: this.searchStartDate || undefined,
      endDate: this.searchEndDate || undefined,
      page: 0,
      size: this.pageSize
    }).pipe(takeUntil(this.destroy$)).subscribe({
      next: (page: any) => {
        this.payments = page.content || page;
        this.dataSource.data = this.payments;
        this.totalElements = page.totalElements || this.payments.length;
        this.isLoading = false;
      },
      error: () => {
        this.snackBar.open('Search failed', 'Close', { duration: 3000 });
        this.isLoading = false;
      }
    });
  }

  loadByMethod(method: string): void {
    this.isLoading = true;
    this.paymentService.getPaymentsByMethod(method).pipe(takeUntil(this.destroy$)).subscribe({
      next: (payments) => {
        this.payments = payments;
        this.dataSource.data = payments;
        this.totalElements = payments.length;
        this.isLoading = false;
      },
      error: () => this.isLoading = false
    });
  }

  loadByDateRange(): void {
    if (!this.searchStartDate || !this.searchEndDate) {
      this.snackBar.open('Please select both start and end dates', 'Close', { duration: 3000 });
      return;
    }
    this.isLoading = true;
    this.paymentService.getPaymentsByDateRange(this.searchStartDate, this.searchEndDate).pipe(takeUntil(this.destroy$)).subscribe({
      next: (payments) => {
        this.payments = payments;
        this.dataSource.data = payments;
        this.totalElements = payments.length;
        this.isLoading = false;
      },
      error: () => this.isLoading = false
    });
  }

  loadData(): void {
    let status: string | undefined;
    switch(this.selectedTabIndex) {
      case 1: status = 'PENDING'; break;
      case 2: status = 'COMPLETED'; break;
      case 3: status = 'REFUNDED'; break;
    }

    // If status is provided, and we want to use the specific endpoint:
    this.isLoading = true;
    if (status) {
      if (status === 'all') {
         this.paymentService.getPayments(this.pageIndex, this.pageSize).pipe(takeUntil(this.destroy$)).subscribe(page => {
          this.payments = page;
          this.dataSource.data = this.payments;
          this.totalElements = page?.length;
          this.loadCustomerNames(this.payments);
          this.isLoading = false;
        });
      } else {
        this.paymentService.getPaymentsByStatus(status).pipe(takeUntil(this.destroy$)).subscribe(payments => {
          this.payments = payments;
          this.dataSource.data = this.payments;
          this.totalElements = payments.length;
          this.loadCustomerNames(this.payments);
          this.isLoading = false;
        });
      }
    } else {
       // Default load
       this.paymentService.getPayments(this.pageIndex, this.pageSize).pipe(takeUntil(this.destroy$)).subscribe(page => {
        this.payments = page;
        this.dataSource.data = this.payments;
        this.totalElements = page?.length;
        this.loadCustomerNames(this.payments);
        this.isLoading = false;
      });
    }
  }

  loadCustomerNames(payments: Payment[]): void {
    const orderIds = [...new Set(payments.map(p => p.orderId).filter((id): id is number => !!id && !this.customerNames.has(id)))];
    
    if (orderIds.length === 0) return;

    const requests = orderIds.map(id => 
      this.orderService.getOrder(id).pipe(
        catchError(() => of(null))
      )
    );

    forkJoin(requests).pipe(takeUntil(this.destroy$)).subscribe(orders => {
      orders.forEach((order, index) => {
        const originalId = orderIds[index];
        if (order && order.id) {
          this.customerNames.set(order.id, order.customerName || 'Anonymous');
        } else {
          // Fallback if order fetch fails
          this.customerNames.set(originalId, 'Unknown');
        }
      });
    });
  }

  loadStats(): void {
    const today = new Date();
    // Start of today (00:00:00)
    const startObj = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    // Current time
    const endObj = new Date();

    const formatDate = (date: Date) => {
      const pad = (n: number) => n.toString().padStart(2, '0');
      return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
    };

    const start = formatDate(startObj);
    const end = formatDate(endObj);
    
    this.paymentService.getStatistics(start, end).pipe(takeUntil(this.destroy$)).subscribe(stats => {
      this.stats.totalRevenue = stats.totalRevenue || 0;
      this.stats.totalTransactions = stats.totalPayments || 0;
      this.stats.totalTips = 0; 
      this.stats.statusCounts = stats.statusCounts || {};
      this.stats.pendingCount = (this.stats.statusCounts['PENDING'] || this.stats.statusCounts['pending'] || 0); 
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onBack(): void {
    this.router.navigate(['/service-provider']);
  }

  onTabChange(event: any): void {
    this.selectedTabIndex = event.index;
    this.pageIndex = 0; 
    this.loadData();
  }
  
  onPageChange(event: PageEvent): void {
    this.pageIndex = event.pageIndex;
    this.pageSize = event.pageSize;
    this.loadData();
  }

  // Statistics getters
  getTotalRevenue(): number { return this.stats.totalRevenue; }
  getTotalTips(): number { return this.stats.totalTips; }
  getTotalTransactions(): number { return this.stats.totalTransactions; }
  getPendingPayments(): number { return this.stats.pendingCount; }
  
  getPaymentsByStatus(status: string): Payment[] {
    
    // Normalize status to uppercase for comparison as backend uses uppercase
    const statusUpper = status.toUpperCase();

    if (this.selectedTabIndex === 0) { 
        // All tab -> filter client side
        return this.payments.filter(p => p.status === statusUpper);
    }
    
    // For specific tabs, we assume loadData fetched the right content (full list)
    // We apply client-side pagination here since the API returns the full list
    const start = this.pageIndex * this.pageSize;
    const end = start + this.pageSize;
    
    if (statusUpper === 'PENDING' && this.selectedTabIndex === 1) return this.payments.slice(start, end);
    if (statusUpper === 'COMPLETED' && this.selectedTabIndex === 2) return this.payments.slice(start, end);
    if (statusUpper === 'REFUNDED' && this.selectedTabIndex === 3) return this.payments.slice(start, end);
    
    return [];
  }

  getMethodColor(method: string): string {
    switch(method?.toLowerCase()) {
      case 'card': return 'primary';
      case 'cash': return 'accent';
      case 'digital': return 'primary';
      default: return 'primary';
    }
  }

  getMethodIcon(method: string): string {
    switch(method?.toLowerCase()) {
      case 'card': return 'credit_card';
      case 'cash': return 'payments';
      case 'digital': return 'phone_android';
      default: return 'payment';
    }
  }

  getStatusColor(status: string): string {
    switch(status?.toUpperCase()) {
      case 'COMPLETED': return 'primary';
      case 'PENDING': return 'warn';
      case 'PROCESSING': return 'accent';
      case 'FAILED': return 'warn';
      case 'REFUNDED': return 'primary';
      default: return 'primary';
    }
  }

  getStatusIcon(status: string): string {
    switch(status?.toUpperCase()) {
      case 'PENDING': return 'schedule';
      case 'PROCESSING': return 'schedule';
      case 'COMPLETED': return 'check_circle';
      case 'FAILED': return 'error';
      case 'REFUNDED': return 'undo';
      default: return 'help';
    }
  }

  // Action methods
  openProcessPaymentDialog(): void {
    const dialogRef = this.dialog.open(PaymentProcessDialogComponent, {
      width: '500px',
      data: {}
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.loadData();
        this.loadStats(); 
      }
    });
  }

  viewPaymentDetail(payment: Payment): void {
    this.dialog.open(PaymentDetailDialogComponent, {
      width: '600px',
      data: payment
    });
  }

  updatePaymentStatus(payment: Payment, newStatus: string): void {
    // Open confirmation dialog first
    const dialogRef = this.dialog.open(PaymentConfirmationDialogComponent, {
      width: '600px',
      data: { payment, newStatus }
    });

    dialogRef.afterClosed().subscribe(confirmed => {
      if (confirmed) {
        // Use the specific status update endpoint
        this.paymentService.updatePaymentStatus(payment.id.toString(), newStatus).subscribe(() => {
          this.snackBar.open('Payment status updated successfully', 'Close', { duration: 3000 });
          // Manually update local state to reflect change immediately if we want responsive UI, or reload
          payment.status = newStatus;
          this.loadData();
          this.loadStats();
        });
      }
    });
  }

  refundPayment(payment: Payment): void {
    const dialogRef = this.dialog.open(PaymentRefundDialogComponent, {
      width: '500px',
      data: payment
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.paymentService.refundPayment(payment.id.toString(), result.amount, result.reason).subscribe(() => {
          this.snackBar.open('Payment has been refunded', 'Close', { duration: 3000 });
          this.loadData();
          this.loadStats();
        });
      }
    });
  }
}